import { Injectable } from '@angular/core';
import { Place } from 'app/core/interfaces/place.interface';
import { AbstractSandbox } from 'app/core/sandboxes/abstract.sandbox';
import { ActiveCustomerService } from 'app/core/services/active-customer.service';
import { ContractInfoCharacteristics, ContractInfoService } from 'app/core/services/contract-info.service';
import { CustomerInfoService } from 'app/core/services/customer-info.service';
import { OrderRestService } from 'app/core/services/rest/order.rest.service';
import { TemplateRestService } from 'app/core/services/rest/template.rest.service';
import { Definition, isDefinition } from 'app/core/store/definitions/definition.interface';
import * as ccrActions from 'app/core/store/edit-ccr-detail/ccr-detail.actions';
import { CCRDetailOpenAction } from 'app/core/store/edit-ccr-detail/ccr-detail.actions';
import { CalculatedTruckLoad, OrderDetail, OrderDetailTO, OrderMaterial } from 'app/core/store/edit-florder-detail/florder-detail.interface';
import { SimplifiedEmailActor } from 'app/core/store/email-actors/email-actors.interface';
import * as actions from 'app/core/store/order-detail/order-detail.actions';
import * as orderActions from 'app/core/store/order-detail/order-detail.actions';
import { OrderDetailCreateFromTemplateAction, OrderDetailCreateStartAction } from 'app/core/store/order-detail/order-detail.actions';
import { RecurrenceDate } from 'app/core/store/order-detail/order-detail.interface';
import { Customer } from 'app/core/store/session/user-context/user-context.interface';
import * as templateActions from 'app/core/store/template/template.actions';
import { OrderTemplateCreateStartAction, OrderTemplateEditAction } from 'app/core/store/template/template.actions';
import { BusyAction, BusyDoneAction } from 'app/core/store/ui/ui.actions';
import { logger } from 'app/core/util/logger';
import { getFirst$$, safe } from 'app/core/util/util';
import { from, Observable, Subject, switchMap } from 'rxjs';
import { distinctUntilChanged, filter, first, map, withLatestFrom } from 'rxjs/operators';
import { goAction } from '../store/router/router.actions';

@Injectable({ providedIn: 'root' })
export class OrderSandbox extends AbstractSandbox {
  isEpsUser$ = this.store.pipe(map(state => state.session.userContext.isEpsUser));
  customersAllowedCreateOrUpdate$ = this.store.pipe(map(state => state.session.customerPermissions.data.createOrUpdateOrder));
  defaultDepot$ = this.store.pipe(
    map(state => {
      const place = Object.keys(state.customerInfo.places)
        .map(key => state.customerInfo.places[key])
        .find(place => place.default);
      return place;
    })
  );
  canSelectDepot$ = this.store.pipe(map(state => state.session.userContext.canSelectDepot));
  isSuperUser: boolean;
  private places: { [p: string]: Place };
  private customerIds: string[] = [];
  private customers: Customer[] = [];

  constructor(
    private contractInfoService: ContractInfoService,
    private orderRestService: OrderRestService,
    private templateRestService: TemplateRestService,
    private customerInfoService: CustomerInfoService,
    private activeCustomerService: ActiveCustomerService,
  ) {
    super();
    this.store.subscribe(state => {
      this.places = state.customerInfo && state.customerInfo.places;
      this.isSuperUser = state.session.userContext.isSuperUser;
    });
    this.store
      .pipe(
        map(state => ({
          userContext: state.session.userContext,
          selectedCustomer: state.session.selectedCustomer.data,
          allowedSUCustomers: state.session.customerPermissions.data.queryOrGetOrder,
        })),
        distinctUntilChanged()
      )
      .subscribe(({ userContext, selectedCustomer, allowedSUCustomers }) => {
        this.customerIds = userContext && userContext.isSuperUser ? allowedSUCustomers.map(c => c.id) : [];
        this.customers = (userContext && userContext.customers) || (selectedCustomer && [selectedCustomer]) || [];
      });
  }

  loadContractInfoFromOrderTypeToIncoterm(orderTypeId: string): Observable<void> {
    if (!orderTypeId) {
      this.azureMonitoringService.logEvent('loadContractInfoFromOrderTypeToIncoterm from orderSandbox.loadContractInfoFromOrderTypeToIncoterm(): orderType is null. Will fail !');
    }
    return this.contractInfoService.loadContractInfoFromOrderTypeToIncoterm('ORDER', orderTypeId);
  }

  loadContractInfoFromFromToIncoterm(params: { orderTypeId: string; shippingCondition: string; fromId: string }) {
    return this.contractInfoService.loadContractInfoFromFromToIncoterm({
      ...params,
      type: 'ORDER',
    });
  }

  loadContractInfoMaterials(params: { orderTypeId: string; shippingCondition: string; fromId: string; toId: string; incoterm: string }): Observable<void> {
    return this.contractInfoService.loadContractInfoMaterials({
      ...params,
      type: 'ORDER',
    });
  }

  /**
   * Copy an order, without using effects
   */
  async copyOrder(etmOrderNumber: string) {
    const subject = new Subject<void>();
    this.store.dispatch(new orderActions.OrderDetailCopyOrderAction({ etmOrderNumber }));
    // Get order
    /*
    I got more info from Piet regarding the 'X'.
    When requesting an order for copy or template, you need the order as it was created -> use the X
    When wanting to see the definitive version of the order, give salesOrderNr, because SAP will fill in other fields (like incoterm) and such.
     */
    this.orderRestService
      .getOrder(etmOrderNumber, 'X', this.customerIds) // UAT 1840 + explanation Piet Borgman, mail 10/08/18 16:39 see mail-piet-2018-08-10-copy-order.png
      .pipe(
        withLatestFrom(this.isEpsUser$, (result, isEpsUser) => ({
          result,
          isEpsUser,
        }))
      )
      .subscribe(async ({ result, isEpsUser }) => {
        // Check if fromId & toId is known in contract
        // IF transport by EPS, then fromId or toId is 'all': depot in contract can change, but doesn't matter for customer
        await this.activeCustomerService.setActiveCustomer(result.soldTo);
        // await this.activeCustomerService.setActiveCustomer(customer);
        if (!this.activeCustomerService.isEpsUserWithDepot() && result.transport === 'Z1') {
          switch (result.orderType) {
            case 'EPS-CUS':
              result.from.id = 'all';
              break;
            case 'CUS-EPS':
              result.to.id = 'all';
              break;
          }
        }

        this.contractInfoService.loadContractInfoAndCheck(this.orderTypeTOToContractInfoCharacteristics(result)).subscribe(isValid => {
          if (isValid) {
            this.store.pipe(first()).subscribe(state => {
              // Store orderDetail in state
              this.store.dispatch(
                new orderActions.OrderDetailCopyOpenSuccessAction({
                  orderDetail: result,
                  definitions: state.definitions,
                })
              );
              // do truck load calculation
              this.calculateTruckLoad(false).subscribe(() => {
                // load delivery-dates
                this.getDeliveryDates().subscribe(() => {
                  // Finish observable
                  this.store.dispatch(
                    goAction({
                      path: ['/orders/copy'],
                      query: { back: 'orders' },
                    })
                  );
                  subject.next();
                  subject.complete();
                });
              });
            });
          } else {
            this.handleFail({ messageCode: 'ERRORS.COPY_ORDER.NO_VALID_CONTRACT' }, 'ORDERS.DETAIL.LABELS.COPY_ORDER');
          }
        });
      });
    return subject.asObservable();
  }

  findPlace(def: Definition): Place {
    return this.places && this.places[def.id];
  }

  createOrderFromTemplate({ date, templateId, customer }: { date: string; templateId: number; customer: Customer }) {
    const subject = new Subject();
    // Get template
    this.templateRestService.getTemplate('ORDER', templateId).subscribe(async template => {
      logger.debug(`just got template`);

      const ci: ContractInfoCharacteristics = {
        type: 'ORDER',
        orderType: template.data.orderType,
        shippingCondition: template.data.transport,
        fromId: template.data.from.id,
        toId: template.data.to.id,
        incoterm: template.data.incoterm,
      };

      this.contractInfoService.loadContractInfoAndCheck(ci).subscribe(isValid => {
        logger.debug(`just checked contractinfo`);
        if (isValid) {
          this.store.pipe(first()).subscribe(state => {
            // Store orderDetail in state
            this.store.dispatch(
              new OrderDetailCreateFromTemplateAction({
                template,
                date,
                customer,
                definitions: state.definitions,
              })
            );
            // do truck load calculation
            logger.debug(`dispatched OrderDetailCreateFromTemplate`);
            this.calculateTruckLoad(false).subscribe(() => {
              logger.debug(`loaded truckload`);
              // load delivery-dates
              this.getDeliveryDates().subscribe(() => {
                this.store.dispatch(
                  goAction({
                    path: ['/orders/copy'],
                    query: { back: 'orders' },
                  })
                );

                subject.next(true);
                subject.complete();
              });
            });
          });
        } else {
          this.handleFail({ messageCode: 'ERRORS.CREATE_ORDER_FROM_TEMPLATE.NO_VALID_CONTRACT' }, 'ERRORS.ORDER.CREATE_FROM_TEMPLATE.TITLE');
          subject.error(null);
        }
      });
    });
    return subject.asObservable();
  }

  editTemplate(templateId: number): Observable<boolean> {
    logger.debug('in editTemplate');
    const subject = new Subject<boolean>();
    // Load template if not yet loaded
    this.store.pipe(first()).subscribe(state => {
      // Function to continue once the template has been loaded
      function proceedWithTemplate(contractInfoCharacteristics: ContractInfoCharacteristics, cb: Function) {
        this.contractInfoService
          .loadContractInfoAndCheck(contractInfoCharacteristics, 'ERRORS.EDIT_TEMPLATE.NO_VALID_CONTRACT', 'ERRORS.TEMPLATE.EDIT.TITLE')
          .subscribe(isValid => {
            if (isValid) {
              if (cb) {
                cb();
              }
              // do truck load calculation
              logger.debug(`dispatched OrderTemplateEditAction`);
              this.calculateTruckLoad(false).subscribe(() => {
                logger.debug(`loaded truckload`);
                // load delivery-dates
                this.getDeliveryDates().subscribe(() => {
                  // Finish observable
                  logger.debug(`fetched deliverydates`);
                  subject.next(true);
                  subject.complete();
                });
              });
            } else {
              this.notificationService.showNotification({
                type: 'error',
                titleCode: 'ERRORS.TEMPLATE.EDIT.TITLE',
                messageCode: 'ERRORS.TEMPLATE.EDIT.CONTRACT_CHANGED',
                modal: true,
                disableClose: false,
              });
              subject.next(false);
              subject.complete();
            }
          });
      }

      if (!state.editOrderDetail.orderDetail || state.editOrderDetail.orderDetail.templateId !== templateId) {
        this.templateRestService.getTemplate('ORDER', templateId).subscribe(async template => {
          const customer = await this.findAndSetActiveCustomer$$(template.data.soldTo);

          proceedWithTemplate.call(this, this.orderTypeTOToContractInfoCharacteristics(template.data), () => {
            this.store.dispatch(
              new OrderTemplateEditAction({
                template,
                definitions: state.definitions,
                customer,
              })
            );
          });
        });
      } else {
        proceedWithTemplate.call(this, this.orderDetailToContractInfoCharacteristics(state.editOrderDetail.orderDetail));
      }
    });

    // Take loaded one and validate
    this.store
      .pipe(
        map(state => state.editOrderDetail.orderDetail),
        filter(orderDetail => !orderDetail || orderDetail.templateId === templateId),
        first()
      )
      .subscribe(state => { });

    return subject.asObservable();
  }

  createTemplateFromOrder(param: { etmOrderNumber: string }) {
    const subject = new Subject<boolean>();
    this.store.pipe(first()).subscribe(state => {
      // store start in store
      this.store.dispatch(new templateActions.TemplateCreateFromOrderStartAction({ etmOrderNumber: param.etmOrderNumber }));
      // Get order
      this.orderRestService.getOrder(param.etmOrderNumber, 'X', this.customerIds).subscribe(async result => {
        await this.activeCustomerService.setActiveCustomer(result.soldTo);
        if (!state.session.userContext.isEpsUser && result.transport === 'Z1') {
          switch (result.orderType) {
            case 'EPS-CUS':
              result.from.id = 'all';
              break;
            case 'CUS-EPS':
              result.to.id = 'all';
              break;
          }
        }

        this.contractInfoService.loadContractInfoAndCheck(this.orderTypeTOToContractInfoCharacteristics(result)).subscribe(isValid => {
          if (isValid) {
            // Store result in store
            this.store.dispatch(
              new templateActions.TemplateCreateFromOrderSuccessAction({
                orderDetail: result,
                definitions: state.definitions,
              })
            );
            this.calculateTruckLoad(false).subscribe(() => {
              logger.debug(`loaded truckload`);
              // load delivery-dates
              this.getDeliveryDates().subscribe(() => {
                // Finish observable
                logger.debug(`fetched deliverydates`);
                subject.next(true);
                subject.complete();
                this.store.dispatch(goAction({ path: '/orders/template/copy' }));
              });
            });
          } else {
            this.handleFail({ messageCode: 'ERRORS.CREATE_TEMPLATE_FROM_ORDER.NO_VALID_CONTRACT' }, 'ORDERS.LIST.BUTTONS.AS_TEMPLATE');
            subject.next(false);
            subject.complete();
          }
        });
      });
    });
    return subject.asObservable();
  }

  openCcrDetail(params: { etmOrderNumber: any; salesOrderNumber: any; createOrEdit: 'create' | 'edit'; ccrNumber?: any }) {
    const subject = new Subject<boolean>();

    this.store.dispatch(
      new CCRDetailOpenAction({
        salesOrderNumber: params.salesOrderNumber,
        etmOrderNumber: params.etmOrderNumber,
        createOrEdit: params.createOrEdit,
      })
    );

    this.store.pipe(first()).subscribe(state => {
      this.orderRestService.getOrder(params.etmOrderNumber, params.salesOrderNumber, this.customerIds).subscribe(
        async result => {
          await this.activeCustomerService.setActiveCustomer(result.soldTo);
          this.store.dispatch(
            new ccrActions.CCRDetailOpenSuccessAction({
              orderDetail: result,
              definitions: state.definitions,
              createOrEdit: params.createOrEdit,
            })
          );

          this.store
            .pipe(
              map(state => state.editCCRDetail),
              filter(editCCRDetail => !editCCRDetail.mode.loading),
              first()
            )
            .subscribe(editCCRDetail => {
              if (editCCRDetail.mode.loadSuccess) {
                this.contractInfoService.loadContractInfoAndCheck(this.orderDetailToContractInfoCharacteristics(editCCRDetail.orderDetail)).subscribe(isValid => {
                  if (!isValid) {
                    this.handleFail({ messageCode: 'ERRORS.CREATE_CCR.NO_VALID_CONTRACT' }, 'ORDERS.FILTER.LABELS.CREATE_CCR');
                  }
                  subject.next(isValid);
                  subject.complete();
                });
              } else {
                this.azureMonitoringService.logException(new Error('ccr open detail fail'), { message: 'open ccr detail fail: loadSuccess = false' });
                this.store.dispatch(new ccrActions.CCRDetailOpenFailAction(null));
                subject.next(false);
                subject.complete();
              }
            });
        },
        error => {
          this.azureMonitoringService.logException(error, { message: 'ccr open detail fail' });
          this.store.dispatch(new ccrActions.CCRDetailOpenFailAction(error));
          subject.error(error);
        }
      );
    });
    return subject.asObservable();
  }

  resetCustomerInfo(): void {
    this.customerInfoService.resetCustomerInfo();
  }

  loadCustomerInfo(customer: Customer, forceLoad = false): Observable<void> {
    return from(this.activeCustomerService.setActiveCustomer(customer)).pipe(
      switchMap(() => this.customerInfoService.loadCustomerInfo(customer, forceLoad))
    );
  }

  showWarning(titleCode: string, messageCode: string) {
    this.notificationService.showModal({
      titleCode,
      messageCode,
      disableClose: false,
      subMessages: [],
      type: 'error',
    });
  }

  resetCreatePage(isTemplate: boolean) {
    this.store.pipe(first()).subscribe(state => {
      const selectedCustomer = state.session.selectedCustomer.data;
      if (!this.isSuperUser) {
        this.store.dispatch(goAction({ path: '/orders' }));
      } else if (isTemplate) {
        this.store.dispatch(
          new OrderTemplateCreateStartAction({
            selectedCustomer,
            isSuperUser: this.isSuperUser,
          })
        );
      } else {
        this.store.dispatch(
          new OrderDetailCreateStartAction({
            selectedCustomer,
            date: null,
            restrictions: state.customerInfo.restrictions,
            preventCheckingContractDetails: true,
            isSuperUser: this.isSuperUser,
          })
        );
      }
    });
  }

  async showEmptyContractModal(isTemplate: boolean) {
    await this.notificationService.showModal({
      type: 'error',
      titleCode: 'WARNING',
      messageCode: 'ORDERS.NOTIFICATIONS.EMPTY_CONTRACT',
      buttonText: 'CLOSE',
      disableClose: false,
    });
    this.resetCreatePage(isTemplate);
  }

  private calculateTruckLoad(showNotification: boolean): Observable<void> {
    const subject = new Subject<void>();
    logger.debug(`just before dispatch OrderDetailLoadTruckCalculate`);
    setTimeout(() => {
      // Ensure reducers have processed
      this.store.dispatch(new orderActions.OrderDetailLoadTruckCalculateAction());
      this.store
        .pipe(
          map(state => state.editOrderDetail.orderDetail),
          first()
        )
        .subscribe((orderDetail: OrderDetail) => {
          logger.debug(`just before checkPalletFloorQuantity`);
          this.orderRestService.checkPalletFloorQuantity(orderDetail).subscribe(
            (result: CalculatedTruckLoad) => {
              // Store result
              this.store.dispatch(new orderActions.OrderDetailLoadTruckCalculateSuccessAction(result));

              if (showNotification) {
                const overload = result.message && (result.message.type === 'E' || result.message.type === 'I' || result.message.type === 'W');
                if (overload) {
                  const type = result.message.type === 'E' ? 'warning' : 'info';
                  this.notificationService.showModal({
                    type,
                    message: result.message.description,
                    disableClose: false,
                  });
                }
              }
              subject.next();
              subject.complete();
            },
            error => {
              this.handleFail(error, '');
              subject.error(error);
            }
          );
        });
    });
    return subject.asObservable();
  }

  private getDeliveryDates(): Observable<void> {
    const subject = new Subject<void>();
    this.store.dispatch(new orderActions.NEOrderDetailRequestDeliveryDatesAction());
    this.store.pipe(first()).subscribe(state => {
      this.orderRestService.getDeliveryDates(state.editOrderDetail.orderDetail.deliveryMethod).subscribe(
        result => {
          this.store.dispatch(new orderActions.NEOrderDetailRequestDeliveryDatesSuccessAction(result));
          subject.next();
          subject.complete();
        },
        error => {
          this.handleFail(error, 'ORDERS.DETAIL.LABELS.GET_DELIVERY_DATES');
          this.azureMonitoringService.logException(error, { message: 'order get deliverydates fail' });
          this.store.dispatch(new orderActions.NEOrderDetailRequestDeliveryDatesFailAction(error));
          subject.error(error);
        }
      );
    });
    return subject.asObservable();
  }

  private orderTypeTOToContractInfoCharacteristics(orderDetailTO: OrderDetailTO): ContractInfoCharacteristics {
    const fromId = orderDetailTO.transport === 'Z1' && orderDetailTO.orderType === 'EPS-CUS' && !this.activeCustomerService.isEpsUserWithDepot() ? '' : orderDetailTO.from.id;
    const toId = orderDetailTO.transport === 'Z1' && orderDetailTO.orderType === 'CUS-EPS' && !this.activeCustomerService.isEpsUserWithDepot() ? '' : orderDetailTO.to.id;

    return {
      type: 'ORDER',
      orderType: orderDetailTO.orderType,
      shippingCondition: orderDetailTO.transport,
      fromId,
      toId,
      incoterm: orderDetailTO.incoterm || '-',
    };
  }

  private orderDetailToContractInfoCharacteristics(orderDetail: OrderDetail): ContractInfoCharacteristics {
    const fromId = orderDetail.deliveryMethod.shippingCondition.id === 'Z1' && orderDetail.deliveryMethod.type === 'EPS-CUS' ? '' : orderDetail.deliveryMethod.from.id;
    const toId = orderDetail.deliveryMethod.shippingCondition.id === 'Z1' && orderDetail.deliveryMethod.type === 'CUS-EPS' ? '' : orderDetail.deliveryMethod.to.id;
    return {
      fromId,
      toId,
      type: 'ORDER',
      orderType: orderDetail.deliveryMethod.type,
      shippingCondition: orderDetail.deliveryMethod.shippingCondition.id,
      incoterm: (orderDetail.deliveryMethod.incoterm && orderDetail.deliveryMethod.incoterm.id) || '-',
    };
  }

  private async findAndSetActiveCustomer$$(soldTo: string | Definition): Promise<Customer> {
    if (!soldTo) {
      return;
    }

    let customer: Customer;
    if (isDefinition(soldTo)) {
      customer = this.customers.find(cust => cust.id === soldTo.id);
    } else {
      customer = this.customers.find(cust => cust.id === soldTo);
    }

    if (customer) {
      await this.activeCustomerService.setActiveCustomer(customer);
    }

    return customer;
  }

  async submitCreateTemplate(isCreate: boolean, showSuccess = true) {
    try {
      this.store.dispatch(new BusyAction('saving template'));
      const orderDetail = await getFirst$$(this.store.select<OrderDetail>(state => state.editOrderDetail.orderDetail));
      const { error } = await safe(getFirst$$(this.templateRestService.createOrderTemplate('ORDER', orderDetail)));
      if (error) {
        await this.handleFail(error, 'ERRORS.TEMPLATE.CREATE');
      } else if (showSuccess) {
        this.store.dispatch(new BusyDoneAction('saving template'));
        await this.notificationService.showModal({
          type: 'success',
          messageCode: 'TEMPLATES.DETAILS.EDIT_SUCCESS',
          disableClose: false,
          redirectUrl: '/orders',
        });
      }
    } finally {
      this.store.dispatch(new BusyDoneAction('saving template'));
    }
  }

  /**
   * create order and template if requested
   * @param loadingDates
   * @param emailAddresses
   */
  async submitCreateOrder(loadingDates: string[], emailAddresses: SimplifiedEmailActor[]): Promise<boolean> {
    try {
      this.store.dispatch(new BusyAction('saving order'));

      this.store.dispatch(
        new orderActions.OrderDetailCreateSubmitAction({
          emailAddresses: emailAddresses,
          loadingDates: loadingDates,
        })
      );

      let orderDetail = await getFirst$$(this.store.select<OrderDetail>(state => state.editOrderDetail.orderDetail));
      const recurrenceDates = await getFirst$$(this.store.select<RecurrenceDate[]>(state => state.editOrderDetail.recurrenceDates));

      let selectedDates;
      if (recurrenceDates) {
        selectedDates = recurrenceDates.filter(date => date.saving).map(date => date.createDate);
      } else if (Array.isArray(orderDetail.planning.loadingDate)) {
        selectedDates = orderDetail.planning.loadingDate;
      } else {
        const dates = [];
        if (orderDetail.singleRecurrence && parseInt(orderDetail.singleRecurrence, 10) > 0) {
          const nr = parseInt(orderDetail.singleRecurrence, 10);
          for (let index = 0; index < nr; index++) {
            dates.push(orderDetail.planning.loadingDate as string);
          }
        } else {
          dates.push(orderDetail.planning.loadingDate as string);
        }
        selectedDates = dates;
      }
      const customerId = orderDetail.customer.id;
      orderDetail = {
        ...orderDetail,
        planning: {
          ...orderDetail.planning,
          loadingDate: selectedDates,
        },
      };
      const { error, result } = await safe(
        getFirst$$(
          this.orderRestService.createOrder(
            customerId,
            orderDetail,
            emailAddresses
          )
        )
      );
      if (error) {
        this.handleFail(error, 'ORDERS.DETAIL.LABELS.SUBMIT_ORDER');
        this.store.dispatch(new orderActions.OrderDetailCreateSubmitFailAction());
        return false; // fail
      } else {
        if (orderDetail.createTemplate) {
          await this.submitCreateTemplate(true, false);
        }
        this.store.dispatch(
          new actions.OrderDetailCreateSubmitSuccessAction({
            result,
            emailAdresses: emailAddresses.map(email => email.email),
          })
        );
      }
      return true; // success
    } finally {
      this.store.dispatch(new BusyDoneAction('saving order'));
    }
  }

  removeMaterial(material: OrderMaterial) {
    this.store.dispatch(new orderActions.OrderDetailCreateRemoveMaterialAction(material));
  }
}
